import { useId, useMemo } from 'react';
import styles from './DISRadar.module.css';

type DISScores = Readonly<{
  temporalDepth?: number;
  beliefCoherence?: number;
  selfMaintenance?: number;
  behavioralCoupling?: number;
  narrativeIntegration?: number;
}>;

type DISRadarProps = Readonly<{
  scores: DISScores;
}>;

const AXIS_HUES = [350, 20, 40, 55, 280] as const;
const MAX_R = 58;
const NUM_RINGS = 5;
const LABEL_SIZE = 8;
const DOT_R = 3;
const FILL_OPACITY = 0.2;
const RING_OPACITY = 0.06;
const GLOW_R = 6;
const STROKE_COLOR = '#9d8fff';
const STROKE_WIDTH = 2;
const FC = { r: 123, g: 104, b: 238 } as const;

const CX = 120;
const CY = 100;
const START_ANGLE = -Math.PI / 2;
const ANGLE_STEP = (2 * Math.PI) / 5;

type Dimension = Readonly<{
  key: keyof DISScores;
  label: string;
  hue: number;
}>;

const DIMENSIONS: readonly Dimension[] = [
  { key: 'temporalDepth', label: 'Depth', hue: 350 },
  { key: 'beliefCoherence', label: 'Coherence', hue: 20 },
  { key: 'selfMaintenance', label: 'Maintain', hue: 40 },
  { key: 'behavioralCoupling', label: 'Coupling', hue: 55 },
  { key: 'narrativeIntegration', label: 'Narrative', hue: 280 },
] as const;

const polarToXY = (angle: number, radius: number): readonly [number, number] => [
  CX + radius * Math.cos(angle),
  CY + radius * Math.sin(angle),
];

const ringPath = (radius: number): string => {
  const points = Array.from({ length: 5 }, (_, i) => {
    const angle = START_ANGLE + i * ANGLE_STEP;
    return polarToXY(angle, radius);
  });
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`)
    .join(' ') + ' Z';
};

const DISRadar = ({ scores }: DISRadarProps): React.ReactElement => {
  const uniqueId = useId();
  const gradientId = `dis-radar-gradient-${uniqueId}`;
  const glowId = `dis-radar-glow-${uniqueId}`;

  const computed = useMemo(() => {
    const values = DIMENSIONS.map((d) => scores[d.key] ?? 0);

    const scorePoints = values.map((v, i) => {
      const angle = START_ANGLE + i * ANGLE_STEP;
      const r = (v / NUM_RINGS) * MAX_R;
      return polarToXY(angle, r);
    });

    const ringPaths = Array.from({ length: NUM_RINGS }, (_, i) => {
      const r = ((i + 1) / NUM_RINGS) * MAX_R;
      const opacity = RING_OPACITY * (1 + i * 0.5);
      return { r, path: ringPath(r), opacity };
    });

    const axisEndpoints = Array.from({ length: 5 }, (_, i) => {
      const angle = START_ANGLE + i * ANGLE_STEP;
      return polarToXY(angle, MAX_R);
    });

    const tickMarks = Array.from({ length: 5 }, (_, axisIdx) => {
      const angle = START_ANGLE + axisIdx * ANGLE_STEP;
      return Array.from({ length: NUM_RINGS }, (_, ringIdx) => {
        const r = ((ringIdx + 1) / NUM_RINGS) * MAX_R;
        const perpAngle = angle + Math.PI / 2;
        const tickLen = 2;
        const [cx, cy] = polarToXY(angle, r);
        return {
          x1: cx - tickLen * Math.cos(perpAngle),
          y1: cy - tickLen * Math.sin(perpAngle),
          x2: cx + tickLen * Math.cos(perpAngle),
          y2: cy + tickLen * Math.sin(perpAngle),
        };
      });
    });

    const scorePath = scorePoints
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`)
      .join(' ') + ' Z';

    const segments = values.map((v, i) => {
      const nextIdx = (i + 1) % 5;
      const nextV = values[nextIdx];
      if (v === 0 && nextV === 0) return null;

      const angle = START_ANGLE + i * ANGLE_STEP;
      const nextAngle = START_ANGLE + nextIdx * ANGLE_STEP;
      const r1 = (v / NUM_RINGS) * MAX_R;
      const r2 = (nextV / NUM_RINGS) * MAX_R;
      const [x1, y1] = polarToXY(angle, r1);
      const [x2, y2] = polarToXY(nextAngle, r2);

      const avgHue = (AXIS_HUES[i] + AXIS_HUES[nextIdx]) / 2;
      const avg = (v + nextV) / 2 / NUM_RINGS;
      return {
        path: `M${CX},${CY} L${x1},${y1} L${x2},${y2} Z`,
        fill: `hsla(${avgHue}, 55%, ${30 + avg * 35}%, ${FILL_OPACITY + avg * FILL_OPACITY})`,
      };
    });

    const ringLabels = Array.from({ length: NUM_RINGS }, (_, i) => {
      const r = ((i + 1) / NUM_RINGS) * MAX_R;
      const [x, y] = polarToXY(START_ANGLE, r);
      return { x: x + 4, y: y + 3, value: i + 1 };
    });

    const axisLabels = DIMENSIONS.map((d, i) => {
      const angle = START_ANGLE + i * ANGLE_STEP;
      const [x, y] = polarToXY(angle, MAX_R + 14);
      const anchor = x < CX - 2 ? 'end' as const : x > CX + 2 ? 'start' as const : 'middle' as const;
      return { x, y, label: d.label, value: values[i], hue: d.hue, anchor };
    });

    return {
      values,
      scorePoints,
      ringPaths,
      axisEndpoints,
      tickMarks,
      scorePath,
      segments,
      ringLabels,
      axisLabels,
    };
  }, [scores]);

  return (
    <div className={styles.container}>
      <svg viewBox="0 0 240 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="DIS Radar">
        {/* Defs: radial gradient + glow filter */}
        <defs>
          <radialGradient id={gradientId}>
            <stop offset="0%" stopColor={`rgba(${FC.r},${FC.g},${FC.b},0.36)`} />
            <stop offset="100%" stopColor={`rgba(${FC.r},${FC.g},${FC.b},0.06)`} />
          </radialGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation={GLOW_R} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Layer 1: Filled concentric pentagon rings (largest first) */}
        {[...computed.ringPaths].reverse().map(({ r, path, opacity }) => (
          <path
            key={`fill-${r}`}
            d={path}
            fill={`url(#${gradientId})`}
            opacity={opacity}
          />
        ))}

        {/* Layer 2: Ring outlines */}
        {computed.ringPaths.map(({ r, path }) => (
          <path
            key={`outline-${r}`}
            d={path}
            fill="none"
            stroke="#2a2a3a"
            strokeWidth={0.5}
            opacity={0.4}
          />
        ))}

        {/* Layer 3: Ring value labels along top axis */}
        {computed.ringLabels.map(({ x, y, value }) => (
          <text
            key={`ring-label-${value}`}
            x={x}
            y={y}
            fill="#555566"
            fontSize={5.5}
            textAnchor="start"
          >
            {value}
          </text>
        ))}

        {/* Layer 4: Axis spokes */}
        {computed.axisEndpoints.map(([ex, ey], i) => (
          <line
            key={`spoke-${i}`}
            x1={CX}
            y1={CY}
            x2={ex}
            y2={ey}
            stroke="#2a2a3a"
            strokeWidth={0.5}
            opacity={0.3}
          />
        ))}

        {/* Layer 5: Tick marks */}
        {computed.tickMarks.map((axisTicks, axisIdx) =>
          axisTicks.map((tick, ringIdx) => (
            <line
              key={`tick-${axisIdx}-${ringIdx}`}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke="#2a2a3a"
              strokeWidth={0.5}
              opacity={0.5}
            />
          ))
        )}

        {/* Layer 6: Per-axis colored triangular segments */}
        {computed.segments.map((seg, i) =>
          seg !== null ? (
            <path
              key={`segment-${i}`}
              d={seg.path}
              fill={seg.fill}
              filter={`url(#${glowId})`}
            />
          ) : null
        )}

        {/* Layer 7: Score polygon outline with pulse animation */}
        <path
          d={computed.scorePath}
          fill="none"
          stroke={STROKE_COLOR}
          strokeWidth={STROKE_WIDTH}
          strokeLinejoin="round"
          className={styles.disRadarPolygon}
          opacity={FILL_OPACITY + 0.6}
        />

        {/* Layer 8: Radial score-reach lines (colored per axis, opacity scales with score) */}
        {computed.scorePoints.map(([px, py], i) => (
          <line
            key={`reach-${i}`}
            x1={CX}
            y1={CY}
            x2={px}
            y2={py}
            stroke={`hsla(${AXIS_HUES[i]}, 80%, 70%, ${0.15 + (computed.values[i] / NUM_RINGS) * 0.5})`}
            strokeWidth={1.5}
            strokeLinecap="round"
            filter={`url(#${glowId})`}
          />
        ))}

        {/* Layer 9: Score dots (brightness scales with score value) */}
        {computed.scorePoints.map(([px, py], i) => (
          <circle
            key={`dot-${i}`}
            cx={px}
            cy={py}
            r={DOT_R}
            fill={`hsl(${AXIS_HUES[i]}, 70%, ${55 + (computed.values[i] / NUM_RINGS) * 20}%)`}
            data-testid={`score-dot-${i}`}
          />
        ))}

        {/* Layer 10: Axis labels + numeric values */}
        {computed.axisLabels.map(({ x, y, label, value, hue, anchor }) => (
          <g key={`label-${label}`}>
            <text
              x={x}
              y={y}
              fill={`hsl(${hue}, 45%, 72%)`}
              fontSize={LABEL_SIZE}
              fontWeight={600}
              textAnchor={anchor}
              dominantBaseline="central"
              data-testid={`axis-label-${label}`}
            >
              {label}
            </text>
            <text
              x={x}
              y={y + 10}
              fill={`hsl(${hue}, 65%, 80%)`}
              fontSize={7}
              textAnchor={anchor}
              dominantBaseline="central"
            >
              {value.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export { DISRadar };
export type { DISRadarProps, DISScores };
